// Types and constants shared by ClientTickets (customer) and ClientSupportManagement (FNN).

export const CLIENT_STATUSES = ['Submitted', 'Investigating', 'In Progress', 'Implementing', 'Resolved'] as const
export type ClientStatus = typeof CLIENT_STATUSES[number]

export const WORKING_STATUSES: ClientStatus[] = ['Submitted', 'Investigating', 'In Progress', 'Implementing']

export const CLIENT_TICKET_TYPES = ['Issue', 'Bug', 'Enhancement', 'Question', 'Other']
export const CLIENT_ENVIRONMENTS = ['Production', 'Support']
export const PRIORITIES = ['Low', 'Medium', 'High', 'Critical', 'Emergency']

export type Awaiting = 'input' | 'validation' | null

export type ResolutionType =
    | 'customer_confirmed'
    | 'verified_by_fnn'
    | 'closed_by_fnn'
    | 'auto_closed_no_input'
    | 'auto_closed_no_validation'
    | null

export const AUTO_CLOSED: ResolutionType[] = ['auto_closed_no_input', 'auto_closed_no_validation']

export const SURVEY_LABELS: Record<number, string> = {
    1: 'Very dissatisfied',
    2: 'Dissatisfied',
    3: 'Neutral',
    4: 'Satisfied',
    5: 'Very satisfied',
}

export interface SurveyResponse {
    rating: number              // 1–5
    rating_label: string
    comment: string
    created_at: string
}

export interface TicketAttachment {
    attachment_id: number
    file_url: string
    file_name: string
    created_at: string
}

export interface ChatMessage {
    id: number
    body: string
    is_system: boolean
    created_at: string
    author_name: string | null
    author_role: 'client' | 'fnn' | 'system'
    is_mine: boolean
    attachments: TicketAttachment[]
}

/** What the customer sees */
export interface ClientTicket {
    ticket_id: number
    title: string
    description: string
    ticket_type: string
    environment: string
    status: ClientStatus
    priority: string | null
    awaiting_customer: Awaiting
    auto_close_at: string | null          // set while waiting on the customer
    assigned_to_name: string | null
    resolution_notes: string | null
    resolution_type: ResolutionType
    created_at: string
    updated_at: string
    resolved_at: string | null
    can_reopen: boolean
    reopen_deadline: string | null
    survey_available: boolean             // resolved, eligible and not rated yet
    survey: SurveyResponse | null         // rating for the current resolution
    attachments: TicketAttachment[]
    unread_count: number
}

export interface SlaInfo {
    state: 'running' | 'paused' | 'stopped'
    counting_now: boolean                 // false on weekends and holidays
    target_hours: number
    using_default_target: boolean
    elapsed_seconds: number
    remaining_seconds: number
    due_at: string | null
    breached: boolean
    paused_since: string | null
    stopped_at: string | null
}

export interface SlaEvent {
    id: number
    event: 'start' | 'pause' | 'resume' | 'stop' | 'reopen'
    event_label: string
    at: string
    actor_name: string | null
    note: string
}

export interface StaffPermissions {
    is_owner: boolean
    is_receiver: boolean
    is_manager: boolean
    is_leader: boolean
    is_assignee: boolean
    is_member: boolean
    can_view_staff: boolean
    can_triage: boolean
    can_assign: boolean
    can_work: boolean
    can_verify: boolean
    can_resolve: boolean
    can_chat: boolean
}

/** What FNN staff see */
export interface StaffTicket {
    ticket_id: number
    title: string
    description: string
    ticket_type: string
    environment: string
    status: ClientStatus
    priority: string | null
    awaiting_customer: Awaiting
    awaiting_reminders_sent: number
    auto_close_at: string | null
    next_reminder_at: string | null
    organization_name: string | null
    created_by_name: string
    created_by_email: string
    target_department: number | null
    target_department_name: string | null
    target_department_has_leader: boolean
    assigned_to: number | null
    assigned_to_name: string | null
    triaged_by_name: string | null
    triaged_at: string | null
    members_can_assign: boolean
    resolution_notes: string | null
    resolution_type: ResolutionType
    resolution_type_label: string | null
    resolved_by_name: string | null
    is_auto_closed: boolean
    csat_eligible: boolean
    survey: SurveyResponse | null
    created_at: string
    updated_at: string
    resolved_at: string | null
    attachments: TicketAttachment[]
    sla: SlaInfo
    unread_count: number
    permissions: StaffPermissions
    sla_events?: SlaEvent[]
}

export interface StaffMeta {
    is_manager: boolean
    is_receiver: boolean
    receiver_name: string | null
    my_department: string | null
    led_departments: string[]
    sla_target_hours: Record<string, number>
    reopen_window_days: number
    awaiting_auto_close_business_days: number
    awaiting_reminder_business_days: number[]
}

export interface DepartmentOption {
    id: number
    name: string
    leader: number | null
    leader_name: string | null
}

export interface FnnUser {
    id: number
    user_id: number
    username: string
    email: string
    first_name: string
    last_name: string
}